/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createCalendar, type DateValue } from "@internationalized/date";
import { useRef, type RefObject } from "react";
import { useDateField, type AriaDatePickerProps } from "react-aria";
import {
  useDateFieldState as useAriaDateFieldState,
  type DateFieldStateOptions,
} from "react-stately";

import { useLocale } from "@superweb/intl";

import { DateTimeInputSegment } from "./segment";

export const DateInput = ({
  ariaPickerProps,
  firstSegmentRef,
  stretch = false,
  hasIcon = false,
  onChange,
}: {
  ariaPickerProps: AriaDatePickerProps<DateValue>;
  firstSegmentRef?: RefObject<HTMLDivElement>;
  stretch?: boolean;
  hasIcon?: boolean;
  onChange: (hasSegmentsWithValue: boolean) => void;
}) => {
  const locale = useLocale();

  const ref = useRef<HTMLDivElement>(null);

  const ariaProps: DateFieldStateOptions = {
    ...ariaPickerProps,
    locale: locale.toString(),
    createCalendar,

    onKeyUp: (e) => {
      if (
        (e.key >= "0" && e.key <= "9") ||
        e.key === "Backspace" ||
        e.key === "Delete"
      ) {
        const hasSegmentsWithValue = ariaState.segments.some(
          (s) => s.type !== "literal" && !s.isPlaceholder,
        );
        onChange(hasSegmentsWithValue);
      }
    },
  };

  const ariaState = useAriaDateFieldState(ariaProps);

  const { fieldProps } = useDateField(ariaProps, ariaState, ref);

  return (
    <div
      {...fieldProps}
      ref={ref}
      css={{
        marginInlineStart: hasIcon ? "54px" : stretch ? "14px" : "0",
        marginBlockStart: stretch ? "24px" : "0",
        marginBlockEnd: stretch ? "8px" : "0",
      }}
    >
      {ariaState.segments.map((segment, i) => (
        <DateTimeInputSegment
          key={i}
          segment={segment}
          segmentRef={i === 0 ? firstSegmentRef : undefined}
          state={ariaState}
        />
      ))}
    </div>
  );
};
